/* import __COLOCATED_TEMPLATE__ from './theme-summaries.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type { Job } from 'embercom/components/reporting/automation/ai-insights/optimize/jobs';
import {
  loadInsightsSummary,
  type InsightsSummaryResult,
  type LoadInsightsSummaryArgs,
} from 'embercom/components/reporting/automation/ai-insights/conversation-summary-data-resources';
import { taskFor } from 'ember-concurrency-ts';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import { restartableTask } from 'ember-concurrency-decorators';
import { inject as service } from '@ember/service';
import { type TaskGenerator } from 'ember-concurrency';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import type Range from 'embercom/models/reporting/range';
import { type FlexibleQueryTimeRange } from 'embercom/services/reporting-csv-export';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import { type LogicalFilter } from 'embercom/components/reporting/custom/filters';
import { type SummaryTab } from './conversation-explore-panel';
import { extractSummaryCards, type SummaryCard } from './summary-converter';
import type IntlService from 'embercom/services/intl';
import { and } from 'embercom/components/reporting/automation/ai-insights/fin-insights/sankey-config';
import { type ReportState } from 'embercom/components/reporting/custom/report/chart-card';

interface Signature {
  Args: {
    reportState: ReportState;
    selectedJob: Job;
    dateRange: Range;
    onShowConversationExplorer: (
      selectedConversationId: number | null,
      filters: LogicalFilter,
      attributeValuesToExplore: string[],
      summaryType: SummaryTab,
      summaryText: string,
      summaryHeading?: string,
      conversationIdsToLoad?: number[],
    ) => void;
    trackAnalyticsEvent?: (
      action: string,
      object: string,
      extraContext: Record<string, any>,
    ) => void;
  };
}

export default class ThemeSummaries extends Component<Signature> {
  @service declare appService: AppService;
  @service declare reportingChartService: ReportingChartService;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @service declare intl: IntlService;

  fetchSummaryTask = taskFor(this.loadSummary);

  summaryData = trackedTask(this, this.fetchSummaryTask, () => [
    this.processedFilters,
    this.args.dateRange,
    this.args.selectedJob.attributeId,
  ]);

  get timeRange(): FlexibleQueryTimeRange {
    return {
      start: this.args.dateRange.startMoment.valueOf(),
      end: this.args.dateRange.endMoment.valueOf(),
      interval: this.args.dateRange.interval,
      property: this.metric.timeProperty,
    };
  }

  get metric() {
    return this.reportingMetrics.getMetricById('v1.new_conversations');
  }

  get summaryText(): string {
    return this.fetchSummaryTask.lastSuccessful?.value?.summaryText || '';
  }

  get processedSummaryText(): SummaryCard[] {
    return extractSummaryCards(this.summaryText);
  }

  get processedFilters() {
    let filter = {
      type: 'category',
      data: {
        property: this.args.selectedJob.attributeId,
        attribute: this.args.selectedJob.attributeId,
        values: [this.args.selectedJob.attributesValues[0]],
      },
    };
    if (this.args.selectedJob.chartSeries[0].filters) {
      return and(this.args.selectedJob.chartSeries[0].filters, filter);
    } else {
      return and(filter);
    }
  }

  get isLoading() {
    return this.summaryData.isRunning;
  }

  @restartableTask
  *loadSummary(): TaskGenerator<InsightsSummaryResult | null> {
    let params = {
      appId: this.appService.app.id,
      adminId: this.appService.app.currentAdmin.id,
      filter: this.processedFilters,
      timeRange: this.timeRange,
      sortByAttributeId: 'conversation.first_user_conversation_part_created_at',
      sortDirection: 'desc',
      syntheticDataUseCase: this.reportingChartService.syntheticDataUseCase,
      timezone: this.appService.app.timezone,
      summaryType: this.args.selectedJob.summaryType,
      selectedCxAttribute: this.args.selectedJob.attributesValues[0],
      maxConversationCount: 100,
    } as LoadInsightsSummaryArgs;

    return yield loadInsightsSummary(params);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::Optimize::ThemeSummaries': typeof ThemeSummaries;
    'reporting/automation/ai-insights/optimize/theme-summaries': typeof ThemeSummaries;
  }
}
